import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DndContext, PointerSensor, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { leadDaUrl } from '@/leads/url';
import { exigeCampoExtra } from '@/leads/regras';
import Carregando from '@/components/Carregando';
import FiltrosBarra from '@/components/leads/FiltrosBarra';
import LeadCard from '@/components/leads/LeadCard';
import PainelLead from '@/components/leads/PainelLead';
import DialogoNovoLead from '@/components/leads/DialogoNovoLead';
import DialogoEtapa from '@/components/leads/DialogoEtapa';
import { useLeads } from '@/leads/LeadsProvider';
import { ETAPAS_KANBAN, etapaPorId } from '@/lib/constants';
import { CAMPO_MOTIVO, motivoPorId } from '@/leads/motivos';
import { moverEtapa } from '@/services/leadsService';
import { PAGINA, fatiar, proximaPagina, rotuloCarregarMais, temMais } from '@/leads/paginacaoColuna';
import { motivosVisiveis, rotuloMaisMotivos, temMaisMotivos } from '@/leads/motivosColuna';

/**
 * ★ VY-c1 — lista de motivos COMPACTA: os 3 maiores por padrão e "+N motivos" para expandir/recolher
 * (estado local da coluna). Os `n` são da lista inteira de leads; só o número de LINHAS muda — o cabeçalho
 * fica fora do corpo rolável e, com 8 motivos, comia a altura da coluna.
 */
function ListaDeMotivos({ motivos, rotulo, chave = (m) => m.id || '_' }) {
  const [expandida, setExpandida] = useState(false);
  if (motivos.length === 0) return null;
  return (
    <ul className="coluna-motivos" aria-label={rotulo} data-total={motivos.length}>
      {motivosVisiveis(motivos, expandida).map((m) => (
        <li key={chave(m)}><span>{m.rotulo}</span><span>{m.n}</span></li>
      ))}
      {temMaisMotivos(motivos) && (
        <li className="motivos-mais">
          <button type="button" className="motivos-expandir" aria-expanded={expandida} onClick={() => setExpandida((e) => !e)}>
            {expandida ? 'menos' : rotuloMaisMotivos(motivos)}
          </button>
        </li>
      )}
    </ul>
  );
}
import { useAuth } from '@/auth/AuthProvider';
import { aplicarFiltros } from '@/leads/filtros';
import { useTarefasAbertas } from '@/leads/useTarefasAbertas';
import {
  COLUNA_ENCERRADOS, agruparEncerradosPorMotivo, carregarEncerradosAberto, colunaDoLead, salvarEncerradosAberto,
} from '@/leads/encerrados';

const moedaCurta = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

/**
 * VG: nas colunas de "não" o cabeçalho mostra a contagem POR MOTIVO — é onde o
 * gestor vê a lista de motivos funcionando, sem abrir lead por lead.
 */
function contarPorMotivo(leads, etapaId, config) {
  const campo = CAMPO_MOTIVO[etapaId];
  if (!campo) return [];
  const mapa = new Map();
  for (const l of leads) {
    const id = l[campo] ?? '';
    mapa.set(id, (mapa.get(id) ?? 0) + 1);
  }
  return [...mapa.entries()]
    .map(([id, n]) => ({ id, n, rotulo: id ? motivoPorId(config, etapaId, id).rotulo : 'sem motivo' }))
    .sort((a, b) => b.n - a.n);
}

/**
 * ★ VQ-1 — ENCERRADOS: coluna própria no fim do funil, RECOLHIDA por padrão (só
 * o cabeçalho "Encerrados · N"); expande ao clicar e lembra por pessoa. Dentro,
 * agrupados por motivo. Não recebe drop: encerrar é pelo motivo, não arrastando.
 */
function ColunaEncerrados({ leads, aoAbrir, config, uid }) {
  const [aberta, setAberta] = useState(() => carregarEncerradosAberto(uid));
  useEffect(() => { setAberta(carregarEncerradosAberto(uid)); }, [uid]);
  const grupos = agruparEncerradosPorMotivo(leads, config);
  const alternar = () => setAberta((a) => { salvarEncerradosAberto(uid, !a); return !a; });
  // ★ VY: paginação de RENDER — a fatia é sobre a lista ACHATADA na ordem dos grupos, e os
  // grupos só desenham o que está na fatia. `leads.length` e os `g.n` seguem TOTAIS.
  const [carregados, setCarregados] = useState(PAGINA);
  const achatada = grupos.flatMap((g) => g.leads);
  const naFatia = new Set(fatiar(achatada, carregados).map((l) => l.id));
  return (
    <section className={aberta ? 'coluna coluna-encerrados aberta' : 'coluna coluna-encerrados recolhida'} data-coluna={COLUNA_ENCERRADOS}>
      <button type="button" className="coluna-cabecalho coluna-cabecalho-botao" aria-expanded={aberta} onClick={alternar}
        title={aberta ? 'Recolher encerrados' : 'Mostrar encerrados'}>
        <span className="coluna-nome">Encerrados</span>
        <span className="coluna-contagem">{leads.length}</span>
      </button>
      {aberta && <ListaDeMotivos motivos={grupos} rotulo="Motivos em Encerrados" chave={(g) => `${g.etapa}:${g.motivoId || '_'}`} />}
      {aberta && (
        <div className="coluna-corpo">
          {grupos.map((g) => ({ ...g, visiveis: g.leads.filter((l) => naFatia.has(l.id)) })).filter((g) => g.visiveis.length > 0).map((g) => (
            <div key={`${g.etapa}:${g.motivoId || '_'}`} className="grupo-encerrados">
              <p className="grupo-encerrados-titulo">{g.rotulo} · {g.n}</p>
              {g.visiveis.map((lead) => <LeadCard key={lead.id} lead={lead} aoAbrir={aoAbrir} />)}
            </div>
          ))}
          {temMais(achatada, carregados) && (
            <button type="button" className="btn-carregar-mais" onClick={() => setCarregados(proximaPagina)}>
              {rotuloCarregarMais(achatada, carregados)}
            </button>
          )}
          {leads.length === 0 && <p className="coluna-vazia">Nenhum lead encerrado</p>}
        </div>
      )}
    </section>
  );
}

function Coluna({ etapaId, leads, aoAbrir, config }) {
  const { setNodeRef, isOver } = useDroppable({ id: etapaId });
  const etapa = etapaPorId(etapaId);
  // Valor e motivos são sobre TODOS os leads da coluna — a fatia é só do desenho.
  const valor = leads.reduce((soma, l) => soma + Number(l.valorFechado ?? 0), 0);
  const porMotivo = contarPorMotivo(leads, etapaId, config);
  // ★ VY: paginação de RENDER por coluna — desenha os primeiros `carregados` (50, 100, …);
  // trocar filtro/mês NÃO zera (decisão do dono: a fatia se ajusta sozinha). O cartão movido
  // para cá aparece em 1º porque o provider entrega por `atualizadoEm desc`.
  const [carregados, setCarregados] = useState(PAGINA);

  return (
    <section ref={setNodeRef} className={isOver ? 'coluna sobre' : 'coluna'}>
      <header className="coluna-cabecalho" style={{ borderTopColor: etapa.cor }}>
        <span className="coluna-nome">{etapa.label}</span>
        <span className="coluna-contagem">{leads.length}</span>
      </header>
      {etapaId === 'fechado' && valor > 0 && <p className="coluna-valor">{moedaCurta(valor)}</p>}
      <ListaDeMotivos motivos={porMotivo} rotulo={`Motivos em ${etapa.label}`} />
      <div className="coluna-corpo">
        {fatiar(leads, carregados).map((lead) => (
          <LeadCard key={lead.id} lead={lead} aoAbrir={aoAbrir} />
        ))}
        {temMais(leads, carregados) && (
          <button type="button" className="btn-carregar-mais" onClick={() => setCarregados(proximaPagina)}>
            {rotuloCarregarMais(leads, carregados)}
          </button>
        )}
        {leads.length === 0 && <p className="coluna-vazia">Nenhum lead</p>}
      </div>
    </section>
  );
}

export default function FunilPage() {
  const { leads, carregando, erro, clinicaId, autor, equipe, abrirLead, leadAberto, clinica, filtros } =
    useLeads();
  const { usuario } = useAuth();
  // VQ-1: no funil os encerrados SEMPRE entram (na coluna própria), com os demais
  // filtros valendo; quem decide a coluna é `colunaDoLead` (carimbo, não config).
  // ★ PC-4: as abertas da clínica, para "Sem próximo contato" (null = não confiáveis → não filtra).
  const tarefas = useTarefasAbertas(clinicaId);
  const visiveis = aplicarFiltros(leads, { ...filtros, mostrarEncerrados: true }, { comAberta: tarefas.comAberta });
  const encerrados = visiveis.filter((l) => colunaDoLead(l) === COLUNA_ENCERRADOS);
  // ★ PC-4 (defeito da medição §4a): o "X de Y" conta os CARTÕES do quadro — as colunas e a
  // de encerrados. Era o `leadsFiltrados` do provider, sem encerrados: 608 contra 673 no quadro.
  const noQuadro = visiveis.filter((l) => colunaDoLead(l) === COLUNA_ENCERRADOS || ETAPAS_KANBAN.includes(colunaDoLead(l)));
  const [novoAberto, setNovoAberto] = useState(false);
  const [pendente, setPendente] = useState(null);
  const [erroMover, setErroMover] = useState(null);
  const [params, setParams] = useSearchParams();

  /**
   * `/funil?lead=<id>` abre o painel daquele lead — é o link "Abrir no funil"
   * do painel da conversa. Espera os leads carregarem (o painel só mostra o que
   * está em memória) e limpa o parâmetro da URL, senão fechar o painel e
   * recarregar a página o reabriria para sempre. Id desconhecido só limpa.
   */
  useEffect(() => {
    const pedido = leadDaUrl(params);
    if (!pedido || carregando) return;
    if (leads.some((l) => l.id === pedido)) abrirLead(pedido);
    const proximos = new URLSearchParams(params);
    proximos.delete('lead');
    setParams(proximos, { replace: true });
  }, [params, carregando, leads]);

  // Exige um pequeno arraste antes de iniciar o drag, para não sequestrar cliques.
  const sensores = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  async function aoSoltar({ active, over }) {
    if (!over) return;
    const lead = active.data.current?.lead;
    const destino = String(over.id);
    if (!lead || lead.etapa === destino) return;

    // Fechado/perdido e (V4) compareceu/em_negociacao sem negociador exigem campo
    // extra — coletado em diálogo antes de gravar.
    if (exigeCampoExtra(destino, lead)) {
      setPendente({ lead, etapa: destino });
      return;
    }
    setErroMover(null);
    try {
      await moverEtapa(clinicaId, lead, destino, {}, autor);
    } catch (e) {
      setErroMover(e.message ?? 'Não foi possível mover o lead.');
    }
  }

  if (carregando) return <Carregando texto="Carregando o funil…" />;

  return (
    <div className={leadAberto ? 'pagina funil com-painel' : 'pagina funil'}>
      <div className="pagina-principal">
        <header className="pagina-cabecalho com-acao">
          <h1>Funil</h1>
          <button
            type="button"
            className="btn-primario compacto"
            onClick={() => setNovoAberto(true)}
          >
            Novo lead
          </button>
        </header>

        <FiltrosBarra mostrarEtapa={false} opcaoEncerrados={false} filtrados={noQuadro} tarefas={tarefas} atalhoSemProximo />

        {erro && <p className="erro">{erro}</p>}
        {erroMover && <p className="erro">{erroMover}</p>}

        <DndContext sensors={sensores} onDragEnd={aoSoltar}>
          <div className="kanban">
            {ETAPAS_KANBAN.map((etapaId) => (
              <Coluna
                key={etapaId}
                etapaId={etapaId}
                leads={visiveis.filter((l) => colunaDoLead(l) === etapaId)}
                aoAbrir={abrirLead}
                config={clinica?.config ?? null}
              />
            ))}
            <ColunaEncerrados leads={encerrados} aoAbrir={abrirLead} config={clinica?.config ?? null} uid={usuario?.uid} />
          </div>
        </DndContext>
      </div>

      <PainelLead aoPedirEtapa={setPendente} />
      <DialogoNovoLead aberto={novoAberto} aoFechar={() => setNovoAberto(false)} />
      <DialogoEtapa
        grade={clinica?.config?.agendaAvaliacao}
        config={clinica?.config ?? null}
        pendente={pendente}
        aoFechar={() => setPendente(null)}
        clinicaId={clinicaId}
        autor={autor}
        equipe={equipe}
      />
    </div>
  );
}
